import { ImMNElement, MNElement, MNElementContent } from '../elements/elementModelTypes';
import { isTaskList } from '../elements/utils/elementTypeUtils';
import { ImMap, prop, propIn } from '../utils/immutableHelper';

export enum TaskListContentVersion {
    Legacy = 'V1',
    SingleEditor = 'V2',
}

const isSingleEditorVersion = (version: unknown): boolean => version === TaskListContentVersion.SingleEditor;

const getPersistedTaskListContentVersion = propIn(['content', 'version']);

/**
 * Checks whether a task list content payload is in the single-editor format.
 *
 * Takes the content rather than the element, for callers that only hold a content payload - an
 * element creation action, for instance. Prefer `isSingleEditorTaskList` when the element is
 * available, since this can't tell a task list's content from any other element's.
 */
export const isSingleEditorTaskListContent = (content?: MNElementContent | ImMap<MNElementContent>): boolean =>
    isSingleEditorVersion(prop('version', content));

/**
 * Gets the persisted task list content version, defaulting old task lists to the legacy format.
 */
export const getTaskListContentVersion = (element: MNElement | ImMNElement): TaskListContentVersion =>
    isSingleEditorVersion(getPersistedTaskListContentVersion(element))
        ? TaskListContentVersion.SingleEditor
        : TaskListContentVersion.Legacy;

/**
 * Checks whether a task list should render with the single-editor implementation.
 */
export const isSingleEditorTaskList = (element: MNElement | ImMNElement): boolean =>
    isTaskList(element) && getTaskListContentVersion(element) === TaskListContentVersion.SingleEditor;

/**
 * Checks whether a task list is still in the legacy format, so still has child `TASK` elements.
 */
export const isLegacyTaskList = (element: MNElement | ImMNElement): boolean =>
    isTaskList(element) && getTaskListContentVersion(element) === TaskListContentVersion.Legacy;
